/* eslint-disable @typescript-eslint/no-explicit-any */
import * as yup from 'yup'
import { isEmpty } from 'lodash'

import { LIMIT, OptionProps, STATUS, MAINTENANCE_UNIT } from 'types'
import { IStockKeepingUnitForm, IStockKeepingUnitDetail } from './model'
import { StockKeepingUnitService } from '.'
import { LIMIT as MAX_LENGTH } from 'helpers/constants'

type Context = yup.TestContext & { type?: string }

export type queryParamProps = {
  q: string
  _page: number
  _ids: number[]
  _sort: 'ASC' | 'DESC'
  _order: string
  isActive: string | boolean
}

export const initialStockKeepingDetail: IStockKeepingUnitDetail = {
  code: '',
  name: '',
  model: null,
  brand: '',
  costCurrency: '',
  costPrice: null,
  saleCurrency: null,
  salePrice: null,
  maintenancePeriod: null,
  maintenanceUnit: null,
  detail: null,
  isActive: false,
  categoryNameTh: null,
  categoryNameEn: null,
  thumbnail: null,
  photos: null,
  categories: [
    {
      code: '',
      detail: '',
      id: '',
      nameEn: '',
      nameTh: '',
      label: '',
    },
  ],
}

export const getQueryParams = (path: string): queryParamProps => {
  const urlParams = new URLSearchParams(path)
  const whitelist = ['q', '_page', '_ids', '_sort', '_order', 'isActive']

  const queryParams: any[] = []

  whitelist.forEach((item) => {
    queryParams.push(urlParams.get(item))
  })

  const [q, page, ids, sort, order, isActive] = queryParams
  const newIds =
    typeof ids === 'string' && ids ? ids.split(',').map((id) => Number(id)) : []

  return {
    q: q || '',
    _page: Number(page) || 1,
    _ids: newIds,
    _sort: sort || '',
    _order: order || 'ASC',
    isActive: isActive || '',
  }
}

export const generateUrl = (_params: any = {}, _update: any = {}): string => {
  const updateQuery = { ..._params, ..._update }

  return `?${new URLSearchParams(updateQuery).toString()}`
}

export const schemaProductCategoryForm = yup.object().shape({
  name: yup.string().required('sku:productName.error'),
})

export const initialProductCategoryForm = {
  name: '',
  code: '',
  detail: '',
  isActive: true,
}

export const findSelected = (
  options?: OptionProps[] | null,
  key?: string,
): OptionProps | null | undefined => {
  if (!options) {
    return null
  }

  return options.find((item: OptionProps) => item.key === key)
}

export const initialStockKeepingUnitForm: IStockKeepingUnitForm = {
  originalSkuCode: '',
  currentState: 'skuCode',
  isActive: true,
  skuCode: '',
  name: '',
  model: '',
  brand: '',
  categories: [],
  costCurrency: { key: 'THB', label: 'THB' },
  costPrice: '',
  saleCurrency: { key: 'THB', label: 'THB' },
  salePrice: '',
  maintenancePeriod: '',
  maintenanceUnit: null,
  detail: '',
  files: [],
  images: [],
}

export const categorySchema = yup.object().shape({
  id: yup.string(),
})

export const fileListSchema = yup.object().shape({
  url: yup.string(),
})

export const schemaStockKeepingUnitForm = yup.object().shape({
  isActive: yup.boolean(),
  skuCode: yup
    .string()
    .max(20)
    .test('skuCode', async (skuCode, context: Context) => {
      const { path, resolve, createError, parent, type } = context

      if (!skuCode) {
        return resolve(true)
      }

      const isSameOriginalSkuCode = parent?.originalSkuCode === skuCode.trim()

      if (
        parent?.currentState === type ||
        parent?.currentState === 'onSubmit'
      ) {
        const pathname = window.location.pathname
        const matchedSkuId = pathname.match(new RegExp(/sku\/(.*?)\/edit/))
        const skuId = matchedSkuId && matchedSkuId[1]

        const response = await StockKeepingUnitService.findAll({
          code: skuCode.trim(),
          isActive: STATUS.ALL,
          _limit: LIMIT.NO_LIMIT,
        })

        const sku = response.body.find(
          (_sku) => _sku.skuCode.toLowerCase() === skuCode.trim().toLowerCase(),
        )

        if (!sku) {
          return resolve(true)
        }

        if (skuId === `${sku.id}` || isSameOriginalSkuCode) {
          return resolve(true)
        }

        return createError({
          path,
          message: 'sku:skuCode.duplicate',
        })
      }

      return false
    }),
  name: yup
    .string()
    .max(100)
    .required('sku:productName.error')
    .matches(/[^\s]/, 'sku:productName.error'),
  brand: yup.string().max(30),
  categories: yup.array().of(categorySchema),
  maintenancePeriod: yup
    .string()
    .max(2)
    .test(
      'maintenancePeriod',
      async (maintenancePeriod: any, context: Context) => {
        const { path, createError, parent } = context
        const emptyPeriod = isEmpty(maintenancePeriod)
        const emptyParentUnit = isEmpty(parent?.maintenanceUnit)
        const isParentNoneUnit =
          parent?.maintenanceUnit?.key === MAINTENANCE_UNIT.NONE

        if (emptyPeriod && emptyParentUnit) {
          return true
        }

        if (emptyPeriod || emptyParentUnit) {
          if (!emptyParentUnit && isParentNoneUnit) {
            return true
          } else {
            return createError({
              path,
              message: 'sku:maintenanceCycle.error',
            })
          }
        } else {
          return true
        }
      },
    ),
  maintenanceUnit: yup
    .object()
    .shape({
      key: yup.string(),
      label: yup.string(),
    })
    .nullable()
    .test(
      'maintenanceUnit',
      async (maintenanceUnit: string, context: Context) => {
        const { path, createError, parent } = context
        const emptyUnit = isEmpty(maintenanceUnit)
        const emptyParenUnit = isEmpty(parent?.maintenanceUnit)
        const emptyParenPeriod = isEmpty(parent?.maintenancePeriod)
        const isParentNoneUnit =
          parent?.maintenanceUnit?.key === MAINTENANCE_UNIT.NONE

        if (emptyUnit && emptyParenUnit) {
          return true
        }

        if (!maintenanceUnit || emptyParenPeriod) {
          if (!emptyParenUnit && isParentNoneUnit) {
            return true
          } else {
            return createError({
              path,
              message: 'sku:maintenanceCycle.error',
            })
          }
        } else {
          return true
        }
      },
    ),
  detail: yup.string().max(1000),
  files: yup.array().of(fileListSchema),
  costCurrency: yup
    .object()
    .shape({
      key: yup.string(),
      label: yup.string(),
    })
    .nullable(),
  saleCurrency: yup
    .object()
    .shape({
      key: yup.string(),
      label: yup.string(),
    })
    .nullable(),
  costPrice: yup.string().max(MAX_LENGTH.PRICE),
  salePrice: yup.string().max(MAX_LENGTH.PRICE),
})
